import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { HostedAgentListItem } from "@/api/admin/hosted-agents"
import type { HostedAgentHealthState } from "@/api/admin/hosted-agents-health"
import type { AccessPolicyUpdate, HostedAgentInput } from "@/api/admin-api"

import {
  createHostedAgent,
  deleteHostedAgent,
  getHostedAgent,
  getHostedAgentInvoice,
  getHostedAgentRaw,
  getHostedAgents,
  getHostedAgentTemplates,
  getHostedAgentUsage,
  instantiateHostedAgentTemplate,
  listHostedAgentRoutineRuns,
  pauseHostedAgent,
  resumeHostedAgent,
  rotateHostedAgentSecret,
  runHostedAgentRoutineNow,
  saveHostedAgentRaw,
  selftestHostedAgent,
  updateHostedAgent,
  updateHostedAgentAccessPolicy,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { FAST_STALE_TIME, GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

// Hosted Agents (HERMES Fas 5) — query keys are rooted at
// ["admin", "hosted-agents"] so every mutation can invalidate the whole
// domain with one call.
const HOSTED_AGENTS_KEY = ["admin", "hosted-agents"] as const

/** Cadence for the agent list while something about it is still settling. */
export const HOSTED_AGENTS_UNSETTLED_POLL_MS = 10_000

/**
 * Health states that mean "this agent may still change on its own".
 *
 * `healthy` and `disabled` are settled — nothing is going to move without a
 * user action. An ABSENT `health` is deliberately settled too: it means the
 * backend does not compute health at all (older deployments), so asking again
 * every ten seconds would never produce a different answer.
 */
const UNSETTLED_HEALTH: ReadonlySet<HostedAgentHealthState> = new Set([
  "never_seen",
  "degraded",
  "unreachable",
])

/**
 * How often to re-ask for the agent list, or `false` to leave it alone.
 *
 * The health column is computed server-side from the agent's last gateway
 * contact, so it turns green some seconds AFTER the container actually comes
 * up — with no poll and `refetchOnWindowFocus` off globally (lib/react-query.ts)
 * the only way to see an agent become healthy was to reload the page. Polling
 * is therefore tied to whether anything is expected to change: any agent in an
 * unsettled health state, or a lifecycle request in flight (`isBusy`, which the
 * card derives from the tenant-wide active-request query). When everything is
 * settled this returns `false` and the list goes quiet again.
 */
export function hostedAgentsPollInterval(
  agents: HostedAgentListItem[] | undefined,
  isBusy = false
): number | false {
  const unsettled =
    isBusy ||
    (agents ?? []).some((agent) => !!agent.health && UNSETTLED_HEALTH.has(agent.health.state))
  return unsettled ? HOSTED_AGENTS_UNSETTLED_POLL_MS : false
}

export function useAdminHostedAgentsQuery(enabled = true, isBusy = false) {
  return useQuery({
    queryKey: HOSTED_AGENTS_KEY,
    queryFn: async () => {
      const { agents } = await getHostedAgents()
      return agents
    },
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchInterval: (query) => hostedAgentsPollInterval(query.state.data, isBusy),
  })
}

export function useAdminHostedAgentQuery(agentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId],
    queryFn: () => getHostedAgent(agentId ?? ""),
    enabled: enabled && !!agentId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

/**
 * Per-agent usage (tokens/SEK, Fas 6) aggregated from the durable
 * run-posts. One fetch covers every agent — consumers pick their agent out
 * of `agents` by id.
 */
export function useAdminHostedAgentUsageQuery(rangeDays: 7 | 30 = 30, enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, "usage", rangeDays],
    queryFn: () => getHostedAgentUsage(rangeDays),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

/** Invoice basis for one calendar month (#3039). */
export function useAdminHostedAgentInvoiceQuery(month: string, enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, "invoice", month],
    queryFn: () => getHostedAgentInvoice(month),
    enabled: enabled && /^\d{4}-\d{2}$/.test(month),
    staleTime: 60_000,
  })
}

export function useAdminHostedAgentTemplatesQuery(enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, "templates"],
    queryFn: getHostedAgentTemplates,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useAdminHostedAgentRawQuery(agentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "raw"],
    queryFn: () => getHostedAgentRaw(agentId ?? ""),
    enabled: enabled && !!agentId,
    // Raw reads back the concurrency hash — always refetch on open.
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/** One entry in the tool catalog offered by the wizard's Tools tab. */
export type HostedAgentToolCatalogEntry = {
  name: string
  description: string
  tags: string[]
  /**
   * Configurable action names for tools that expose an ACTIONS concept
   * (e.g. `microsoft_365_search` → get_emails/get_files/…); empty for tools
   * without one. Drives the per-action allowlist picker (`tool_actions`).
   */
  actions: string[]
  /**
   * Picker group id derived server-side from the tool's manifest tags
   * (#3022 — `group` on the catalog entry). Empty when the backend predates
   * the field; such tools render under the "other" section.
   */
  group: string
  /**
   * The tool's approval policy gates the WHOLE tool. Hosted agents have no
   * approval channel, so the gateway drops it from the agent's profile and
   * refuses it fail-closed (403 `agent_tool_approval_required`) — selecting
   * it here has no effect.
   */
  requiresApproval: boolean
  /**
   * Individual actions the approval policy gates. They are pruned from the
   * enum the hosted agent sees, independently of the `tool_actions`
   * allowlist; the rest of the tool works.
   */
  approvalGatedActions: string[]
}

/**
 * Tool catalog for the wizard's Tools picker (A3.1). Served by the dedicated
 * `GET /admin/hosted-agents/tools/catalog` endpoint, which projects the
 * agent-runtime tool registry (`get_registry().list_available_tools()` +
 * `resolve_tools`) — the same source `tools[]` is validated against — and
 * carries each tool's configurable `actions`, its picker `group` (#3022) and
 * its approval gating (`requires_approval` / `approval_gated_actions`).
 */
export function useAdminHostedAgentToolCatalogQuery(enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, "tool-catalog"],
    queryFn: async (): Promise<HostedAgentToolCatalogEntry[]> => {
      const response = await api.get("/admin/hosted-agents/tools/catalog")
      const data = response.data || response
      const tools: unknown[] = Array.isArray(data?.tools) ? data.tools : []
      return tools
        .filter(
          (tool): tool is Record<string, unknown> => typeof tool === "object" && tool !== null
        )
        .map((tool) => ({
          name: String(tool.name ?? ""),
          description: typeof tool.description === "string" ? tool.description : "",
          tags: Array.isArray(tool.tags) ? tool.tags.map(String) : [],
          actions: Array.isArray(tool.actions) ? tool.actions.map(String) : [],
          group: typeof tool.group === "string" ? tool.group : "",
          requiresApproval: tool.requires_approval === true,
          approvalGatedActions: Array.isArray(tool.approval_gated_actions)
            ? tool.approval_gated_actions.map(String)
            : [],
        }))
        .filter((tool) => tool.name)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
  })
}

function useInvalidateHostedAgents() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: HOSTED_AGENTS_KEY })
}

export function useCreateHostedAgentMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (payload: HostedAgentInput) => createHostedAgent(payload),
    onSuccess: invalidate,
  })
}

export function useInstantiateHostedAgentTemplateMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: ({
      templateId,
      overrides,
    }: {
      templateId: string
      overrides?: Record<string, unknown>
    }) => instantiateHostedAgentTemplate(templateId, overrides),
    onSuccess: invalidate,
  })
}

export function useUpdateHostedAgentMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: ({
      agentId,
      agent,
      revision,
    }: {
      agentId: string
      agent: Record<string, unknown>
      revision: number
    }) => updateHostedAgent(agentId, agent, revision),
    onSuccess: invalidate,
  })
}

export function useDeleteHostedAgentMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (agentId: string) => deleteHostedAgent(agentId),
    onSuccess: invalidate,
  })
}

export function useUpdateHostedAgentAccessMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: ({ agentId, policy }: { agentId: string; policy: AccessPolicyUpdate }) =>
      updateHostedAgentAccessPolicy(agentId, policy),
    onSuccess: invalidate,
  })
}

export function useSaveHostedAgentRawMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (args: {
      agentId: string
      entity: Record<string, unknown>
      expectedHash: string
      reason: string
      source?: string
    }) => saveHostedAgentRaw(args),
    onSuccess: invalidate,
  })
}

export function usePauseHostedAgentMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (agentId: string) => pauseHostedAgent(agentId),
    onSuccess: invalidate,
  })
}

export function useResumeHostedAgentMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (agentId: string) => resumeHostedAgent(agentId),
    onSuccess: invalidate,
  })
}

export function useRotateHostedAgentSecretMutation() {
  const invalidate = useInvalidateHostedAgents()
  return useMutation({
    mutationFn: (agentId: string) => rotateHostedAgentSecret(agentId),
    onSuccess: invalidate,
  })
}

export function useSelftestHostedAgentMutation() {
  return useMutation({
    mutationFn: ({ agentId, timeoutSeconds }: { agentId: string; timeoutSeconds?: number }) =>
      selftestHostedAgent(agentId, timeoutSeconds),
  })
}

// ---------------------------------------------------------------------------
// Scheduled routines (B1)
// ---------------------------------------------------------------------------

/**
 * Recent routine run-posts for an agent (optionally scoped to one routine).
 * Refetches on window focus so the history panel reflects a just-fired run.
 */
export function useAdminHostedAgentRoutineRunsQuery(
  agentId: string | undefined,
  routineId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "routine-runs", routineId ?? null],
    queryFn: () => listHostedAgentRoutineRuns(agentId ?? "", routineId),
    enabled: enabled && !!agentId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useRunHostedAgentRoutineNowMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, routineId }: { agentId: string; routineId: string }) =>
      runHostedAgentRoutineNow(agentId, routineId),
    onSuccess: (_result, { agentId }) =>
      queryClient.invalidateQueries({
        queryKey: [...HOSTED_AGENTS_KEY, agentId, "routine-runs"],
      }),
  })
}
